<template>
  <base-dialog
    :title="$t('common.action.send') + ' ' + $t('common.action.paymentLink')"
    :actionName="$t('common.action.send')"
    @action="sendPaymentLink()"
    :actionDisabled="isValid()"
    :max-width="$vuetify.display.mdAndUp ? '30%' : '100%'"
    :showCancel="$t('common.action.cancel')"
    @close="showDialog = false"
    :isLoading="isLoading"
    :show="showDialog"
  >
    <template #content>
      <v-btn-toggle v-model="toggle" mandatory class="mx-2 mb-8" @click="toggleChange()">
        <v-btn variant="text" color="primary" value="search" class="mx-2">
          {{ $t("common.action.searchCustomers") }}
        </v-btn>

        <v-btn variant="text" color="primary" value="create">
          {{ $t("page.paymentLinks.addNewCustomer") }}
        </v-btn>
      </v-btn-toggle>
      <customer-search-and-select
        v-if="toggle === 'search'"
        @customer-selected="handleCustomerSelected"
        class="mx-4"
      />
      <v-form ref="customerForm">
        <div class="mx-4">
          <v-row class="d-flex">
            <v-col>
              <p class="text-body-1 mb-1 text-medium-emphasis font-weight-regular">
                {{ $t("common.action.customer") }}
              </p>
            </v-col>
            <v-col class="d-flex justify-end" v-if="customerExists"
              ><v-btn
                v-if="!editingCustomer"
                @click="editingCustomer = true"
                size="x-small"
                variant="text"
                color="primary"
                >{{ $t("common.action.editCustomer") }}</v-btn
              >
              <v-chip size="x-small" color="grey" v-else>
                {{ $t("page.paymentLinks.updatingCustomerMessage") }}</v-chip
              >
            </v-col>
          </v-row>

          <v-text-field
            v-model="name"
            :disabled="!creatingNewCustomer && !editingCustomer"
            :placeholder="$t('common.action.customer') + ' ' + $t('common.action.name')"
            variant="outlined"
            :rules="nameRules"
          />
          <p class="text-body-1 mb-1 text-medium-emphasis font-weight-regular">
            {{ $t("common.action.phone") }}
          </p>
          <v-text-field
            :disabled="!creatingNewCustomer && !editingCustomer"
            v-model="phone"
            :placeholder="$t('common.action.phone')"
            variant="outlined"
            type="number"
          />
          <p class="text-body-1 mb-1 text-medium-emphasis font-weight-regular">
            {{ $t("common.action.email") }}
          </p>
          <v-text-field
            :disabled="!creatingNewCustomer && !editingCustomer"
            v-model="email"
            type="email"
            :placeholder="$t('common.action.email')"
            variant="outlined"
            :rules="[emailFormatRule]"
          />
          <v-row>
            <v-col cols="6">
              <v-checkbox
                v-model="smsSend"
                :disabled="!phone"
                :label="$t('common.action.send') + ' ' + $t('common.action.sms')"
                color="primary"
                density="comfortable"
              />
            </v-col>
            <v-col cols="6">
              <v-checkbox
                v-model="emailSend"
                :disabled="!email"
                :label="$t('common.action.send') + ' ' + $t('common.action.email')"
                color="primary"
                density="comfortable"
              />
            </v-col>
          </v-row>
        </div>
      </v-form>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import { useValidation } from "@/shared/composables/validation";
import { useCustomersStore } from "@/customers/stores/customers";
import { usePaymentLinksStore } from "@/payment-links/stores/paymentLinks";
import { Methods } from "@/shared/enums/twilio.enum";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import i18n from "@/app/plugins/i18n";
import CustomerSearchAndSelect from "@/shared/components/Common/CustomerSearchAndSelect.vue";
import { useAccountStore } from "@/account/stores/account";
const showDialog = ref(false);
const isLoading = ref(false);
const customerExists = ref(false);
const paymentLinkUrl = ref("");
const smsSend = ref(false);
const emailSend = ref(false);
const paymentLinkName = ref("");
const { name, email, phone, emailFormatRule, nameRules } = useValidation();
const customerSelect = ref("");
const creatingNewCustomer = ref(false);
const toggle = ref("search");
const customerForm = ref<{ reset: () => void } | null>(null);
const editingCustomer = ref(false);
const customerId = ref("");
const businessName = useAccountStore().account?.business_profile?.name ?? undefined;

const reset = () => {
  name.value = "";
  email.value = "";
  phone.value = "";
  customerId.value = "";
  isLoading.value = false;
  customerExists.value = false;
  smsSend.value = false;
  emailSend.value = false;
  editingCustomer.value = false;
  toggle.value = "search";
};

const updateCustomerInfo = async () => {
  const payload = {
    name: name.value,
    phone: phone.value,
    email: email.value,
  };
  await useCustomersStore().updateCustomer(customerId.value, payload);
};

const open = async (url: string, name?: string) => {
  reset();
  showDialog.value = true;
  paymentLinkUrl.value = url;
  if (name) {
    paymentLinkName.value = name;
  }
};

defineExpose({
  open,
});

const isValid = () => {
  return !(name.value && (smsSend.value || emailSend.value));
};

// methods
const createCustomer = async () => {
  await useCustomersStore().createCustomer({
    name: name.value,
    ...(email.value ? { email: email.value } : {}),
    ...(phone.value ? { phone: phone.value } : {}),
  });
  useSnackBarStore().toastSuccess({
    message: i18n.global.t("page.paymentLinks.message.customerCreated"),
  });
};

const sendPaymentLink = async () => {
  // Check if the customer exists, if not create a new customer
  isLoading.value = true;
  if (!customerExists.value) {
    await createCustomer();
  }

  if (customerExists.value) {
    await updateCustomerInfo();
  }
  // Send the payment link
  if (smsSend.value) {
    await usePaymentLinksStore().sendPaymentLink({
      name: paymentLinkName.value || i18n.global.t("common.action.paymentLink"),
      phone: phone.value ?? undefined,
      paymentLink: paymentLinkUrl.value,
      methods: Methods.SMS,
      businessName,
    });
  }
  if (emailSend.value) {
    await usePaymentLinksStore().sendPaymentLink({
      name: paymentLinkName.value || i18n.global.t("common.action.you"),
      email: email.value ?? undefined,
      paymentLink: paymentLinkUrl.value,
      methods: Methods.EMAIL,
      businessName,
    });
  }
  isLoading.value = false;

  showDialog.value = false;
};

const handleCustomerSelected = (item: {
  name: string | null;
  email: string | null;
  phone: string | null;
  id: string;
}) => {
  customerForm?.value?.reset();
  reset();
  customerSelect.value = "";
  customerExists.value = true;
  name.value = item.name ?? "";
  email.value = item.email ?? "";
  phone.value = item.phone ?? "";
  customerId.value = item.id;
  toggle.value = "search";
};

const toggleChange = () => {
  customerExists.value = false;
  customerForm.value?.reset();
  customerSelect.value = "";
  creatingNewCustomer.value = toggle.value !== "search";
};
</script>
